import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  fetchSiteConditionState,
  type SiteConditionMeasurementWindow,
  type SiteConditionState,
} from '../../domain/continuousSafety/siteConditionState';

type SiteConditionStatePanelProps = {
  siteId: string;
};

function windowHeadline(window: SiteConditionMeasurementWindow): string {
  if (window.availability === 'unavailable') {
    return 'Unavailable';
  }
  if (window.displayState === 'current') {
    return 'Current';
  }
  if (window.displayState === 'current_with_limitations') {
    return 'Current with limitations';
  }
  if (window.displayState === 'delayed') {
    return 'Delayed';
  }
  if (window.displayState === 'stale') {
    return 'Stale';
  }
  if (window.displayState === 'never_measured') {
    return 'Not measured yet';
  }
  return window.displayState;
}

function WindowCard({
  title,
  window,
  headlineOverride,
  testId,
}: {
  title: string;
  window: SiteConditionMeasurementWindow;
  headlineOverride?: string;
  testId?: string;
}) {
  const value =
    window.summary.measurementValue == null
      ? `${window.summary.bucketCount.toLocaleString()} governed bucket${
          window.summary.bucketCount === 1 ? '' : 's'
        }`
      : window.summary.measurementValue.toLocaleString();
  const activityPosture = Object.entries(window.summary.activityCounts || {})
    .filter(([, count]) => count > 0)
    .map(([activity, count]) => `${activity} ${count}`)
    .join(', ');
  const headline = headlineOverride || windowHeadline(window);
  return (
    <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }} data-testid={testId}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>
        {value}
      </Typography>
      {window.summary.measurementValue == null ? (
        <Typography variant="caption" color="text.secondary" display="block">
          No additive total available{activityPosture ? ` · ${activityPosture}` : ''}
        </Typography>
      ) : null}
      <Chip
        label={headline}
        size="small"
        color={
          !headlineOverride && window.displayState === 'current'
            ? 'success'
            : window.displayState === 'unavailable' || headlineOverride
              ? 'default'
              : 'warning'
        }
        sx={{ mt: 1 }}
      />
      {window.basis?.type ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Basis: {window.basis.type} ({window.basis.version || 'version unavailable'})
        </Typography>
      ) : null}
      {window.reasonCodes.length > 0 ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {window.reasonCodes.join(', ')}
        </Typography>
      ) : null}
    </Paper>
  );
}

function nowHeadlineOverride(condition: SiteConditionState): string | undefined {
  const health = condition.customerHealth;
  const now = condition.operationalMeasurements.now;
  if (!health || health.measurementHealth !== 'unavailable') return undefined;
  if (now.displayState !== 'current' && now.displayState !== 'current_with_limitations') {
    return undefined;
  }
  return health.measurementLabel || 'Measurement unavailable';
}

function ConditionCard({ condition }: { condition: SiteConditionState }) {
  const now = condition.operationalMeasurements.now;
  const rolling24h = condition.operationalMeasurements.overTime.rolling_24h;
  const health = condition.customerHealth;
  return (
    <Stack spacing={1.25} data-testid="site-condition-state-card">
      <Typography variant="body2" color="text.secondary">
        Condition {condition.conditionFamily} · lifecycle {condition.conditionCurrent.lifecycleState}
      </Typography>
      {health ? (
        <Stack
          spacing={0.25}
          data-testid="condition-customer-health"
          data-renders-current-clear={health.rendersCurrentClear ? 'true' : 'false'}
          data-now-claims-current-healthy={health.nowClaimsCurrentHealthy ? 'true' : 'false'}
        >
          <Typography variant="body2" data-testid="condition-measurement-health">
            {health.measurementLabel}
          </Typography>
          <Typography variant="body2" data-testid="condition-video-health">
            {health.videoLabel}
          </Typography>
          {health.evidenceNote ? (
            <Typography variant="caption" color="text.secondary" data-testid="condition-evidence-note">
              {health.evidenceNote}
            </Typography>
          ) : null}
        </Stack>
      ) : null}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 1.25,
        }}
      >
        <WindowCard
          title="Now"
          window={now}
          headlineOverride={nowHeadlineOverride(condition)}
          testId="condition-now-window"
        />
        <WindowCard title="Over time (24h)" window={rolling24h} testId="condition-history-window" />
      </Box>
    </Stack>
  );
}

const SiteConditionStatePanel: React.FC<SiteConditionStatePanelProps> = ({ siteId }) => {
  const [loading, setLoading] = useState(true);
  const [conditions, setConditions] = useState<SiteConditionState[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchSiteConditionState({ siteId })
      .then((result) => {
        if (cancelled) return;
        if (result.status === 'disabled') {
          setEnabled(false);
          setConditions([]);
          return;
        }
        if (result.status === 'error') {
          setEnabled(true);
          setConditions([]);
          setError(result.message);
          return;
        }
        setEnabled(true);
        setConditions(result.data.conditions);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }} data-testid="site-condition-state-panel">
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2">Loading governed site condition measurements…</Typography>
        </Stack>
      </Paper>
    );
  }

  if (!enabled) {
    return null;
  }

  if (error) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }} data-testid="site-condition-state-panel">
        <Alert severity="warning" role="alert">
          {error} No Site Condition risk conclusion is available.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="site-condition-state-panel">
      <Typography variant="h6" sx={{ fontWeight: 800 }}>
        Governed condition measurements
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
        Now and 24-hour views use the continuous safety read API. Values stay hidden when coverage,
        basis, or freshness is not authoritative.
      </Typography>
      {conditions.length === 0 ? (
        <Alert severity="info" data-testid="site-condition-state-empty">
          No governed Site Conditions are available for this site yet.
        </Alert>
      ) : (
        <Stack spacing={2}>
          {conditions.slice(0, 3).map((condition) => (
            <ConditionCard key={condition.conditionId} condition={condition} />
          ))}
        </Stack>
      )}
    </Paper>
  );
};

export default SiteConditionStatePanel;
